import { DEFAULT_SITE, isValidSite, type SiteIdentifier } from '@/constant/siteMapping'

export const SITE_STORAGE_KEY = 'design_center_site'

const WEBSITE_INFO_COOKIE = 'fs_websiteinfo'

const SITE_ROUTE_ALIAS_MAP: Partial<Record<SiteIdentifier, string>> = {
  'de-en': 'eu-en',
  mo: 'hk',
}

const ROUTE_SITE_ALIAS_MAP: Record<string, SiteIdentifier> = {
  'eu-en': 'de-en',
}

const getRuntimePathname = () => (typeof window === 'undefined' ? '/' : window.location.pathname)

export const getSiteFromPath = (pathname = getRuntimePathname()): SiteIdentifier | '' => {
  const firstSegment = pathname.split('/').filter(Boolean)[0]
  if (!firstSegment) return ''

  return ROUTE_SITE_ALIAS_MAP[firstSegment] || (isValidSite(firstSegment) ? firstSegment : '')
}

export const getRouterBase = (pathname = getRuntimePathname()) => {
  const site = getSiteFromPath(pathname)
  return site ? `/${getWebsiteRouteSite(site)}/` : '/'
}

export const getStoredSite = () => {
  try {
    return localStorage.getItem(SITE_STORAGE_KEY)
  } catch {
    return null
  }
}

const decodeCookieValue = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const getCookieValues = (name: string) => {
  if (typeof document === 'undefined') return []

  const encodedName = `${encodeURIComponent(name)}=`
  return document.cookie
    .split(/;\s*/)
    .filter(cookie => cookie.startsWith(encodedName))
    .map(cookie => decodeCookieValue(cookie.slice(encodedName.length)))
}

const normalizeSite = (value: unknown): SiteIdentifier | '' => {
  if (typeof value !== 'string') return ''

  const site = value.trim().toLowerCase()
  return isValidSite(site) ? site : ''
}

export const getSiteFromCookie = (): SiteIdentifier | '' => {
  // fs_websiteinfo is the canonical site cookie shared with the main site.
  // Legacy website/site cookies are only fallbacks because they may not be
  // updated when the user switches sites outside Design Center.
  const websiteInfoValues = getCookieValues(WEBSITE_INFO_COOKIE)
  for (let index = websiteInfoValues.length - 1; index >= 0; index--) {
    try {
      const websiteInfo = JSON.parse(websiteInfoValues[index]) as { website?: unknown; locale?: unknown }
      const site = normalizeSite(websiteInfo.website || websiteInfo.locale)
      if (site) return site
    } catch {
      // Ignore malformed website info cookies and continue checking older values.
    }
  }

  const directSite = [...getCookieValues('website'), ...getCookieValues('site')]
    .map(normalizeSite)
    .filter((site): site is SiteIdentifier => Boolean(site))
    .pop()
  if (directSite) return directSite

  return ''
}

export const persistSite = (site: string) => {
  if (!isValidSite(site)) return

  try {
    localStorage.setItem(SITE_STORAGE_KEY, site)
  } catch {
    // localStorage may be unavailable in privacy mode
  }
}

export const resolveInitialSite = (): SiteIdentifier => {
  const cookieSite = getSiteFromCookie()
  if (cookieSite) {
    persistSite(cookieSite)
    return cookieSite
  }

  const pathSite = getSiteFromPath()
  if (pathSite) {
    persistSite(pathSite)
    return pathSite
  }

  const storedSite = getStoredSite()
  return storedSite && isValidSite(storedSite) ? storedSite : DEFAULT_SITE
}

export const getWebsiteRouteSite = (site: string) => {
  const normalizedSite = site.trim().toLowerCase()
  return SITE_ROUTE_ALIAS_MAP[normalizedSite as SiteIdentifier] || normalizedSite
}

/** The main/default site has no route prefix. */
export const getRouteSite = (site: string) => (site === DEFAULT_SITE ? '' : getWebsiteRouteSite(site))

export const withSitePrefix = (path: string, site = getSiteFromPath()) => {
  if (!site || !path || path.startsWith('#') || /^(?:[a-z]+:)?\/\//i.test(path)) {
    return path
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  const firstSegment = normalizedPath.split('/').filter(Boolean)[0]
  if (firstSegment && (isValidSite(firstSegment) || firstSegment in ROUTE_SITE_ALIAS_MAP)) {
    return normalizedPath
  }

  return `/${getWebsiteRouteSite(site)}${normalizedPath}`
}
